import { Button, Input, Spin } from 'antd';
import { useEffect, useState } from 'react';
import PlacesAutocomplete from 'react-places-autocomplete';
import type { LocationSearchInputViewInterface } from '@/interfaces/views/props/LocationSearchInputViewProps';
import type { LocationSuggestion } from '../hooks/useLocationSearch';

const searchOptions = {
  types: ['(cities)'],
  componentRestrictions: { country: ['pe'] },
  fields: ['geometry', 'address_components'],
};

export const LocationSearchInputView = ({
  value,
  dropdownHidden,
  setDropdownHidden,
  onChange,
  onSelect,
  options,
  inputProps,
  onManually,
}: LocationSearchInputViewInterface) => {
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);

  useEffect(() => {
    const checkGoogleMapsLoaded = () => {
      if (window.google?.maps?.places) {
        setIsGoogleMapsLoaded(true);
      } else {
        setTimeout(checkGoogleMapsLoaded, 100);
      }
    };

    checkGoogleMapsLoaded();
  }, []);

  if (!isGoogleMapsLoaded) {
    return (
      <Input
        {...inputProps}
        placeholder="Loading Google Maps..."
        disabled
        suffix={<Spin size="small" />}
      />
    );
  }

  options = { ...searchOptions, ...options };
  return (
    <PlacesAutocomplete
      value={value || ''}
      debounce={200}
      onSelect={(suggestion) => onSelect(suggestion as unknown as LocationSuggestion)}
      onChange={(v) => onChange(v)}
      searchOptions={options}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <div style={{ position: 'relative' }}>
          <Input
            {...getInputProps({
              placeholder: 'Start typing District or City...',
            })}
            onKeyDown={() => {}}
            onBlur={() => {}}
            {...inputProps}
          />
          <div
            className={`autocomplete-dropdown-container ${dropdownHidden ? 'hidden' : ''}`}
            role="listbox"
            onMouseDown={(e) => e.preventDefault()}
          >
            {loading && <div className="suggestion-item">Loading...</div>}
            {!loading && !dropdownHidden && Object.keys(suggestions).length === 0 && (
              <div className="suggestion-item">No results</div>
            )}
            {suggestions.map((suggestion) => {
              const className = suggestion.active ? 'suggestion-item--active' : 'suggestion-item';
              const style = suggestion.active
                ? { backgroundColor: '#fafafa', cursor: 'pointer' }
                : { backgroundColor: '#ffffff', cursor: 'pointer' };
              return (
                <div
                  {...getSuggestionItemProps(suggestion, {
                    className,
                    style,
                  })}
                  role="option"
                  tabIndex={0}
                  onClick={() => {
                    onSelect(suggestion as unknown as LocationSuggestion);
                    setDropdownHidden(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      onSelect(suggestion as unknown as LocationSuggestion);
                      setDropdownHidden(true);
                    }
                  }}
                  key={suggestion.index}
                >
                  <span>{suggestion.description}</span>
                </div>
              );
            })}
            {onManually && (
              <div className="autocomplete-dropdown-container-tip suggestion-item">
                <span>Couldn't find the address?</span>
                <Button type="link" style={{ fontSize: '1em' }} onClick={() => onManually()}>
                  <span>Enter manually →</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </PlacesAutocomplete>
  );
};
